import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.scss';
import './polyfills/array-methods.ts';
import './polyfills/object-group-by.ts';
import './polyfills/promise-with-resolvers.ts';
import './polyfills/url-parse.ts';

import * as Sentry from '@sentry/react';
import { pdfjs } from 'react-pdf';
import { App } from './App';
import { installFontMirror } from './components/DetachablePanel/utils/fontMirror';
import { MAIN_WINDOW_NAME } from './context/PanelManager/PanelManager';
import { scrubUrl } from './utils/scrubUrl';

window.name = MAIN_WINDOW_NAME;

// Use CDN for PDF.js worker (legacy build for older browser support)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

installFontMirror();

// Sentry Initialisation
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.DEV ? 'local' : import.meta.env.VITE_MODE,
    release: import.meta.env.DEV ? 'local' : import.meta.env.VITE_APP_VERSION,
    sendDefaultPii: true,
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    // Scrubs presigned-URL signatures and auth tokens out of breadcrumb URLs.
    beforeBreadcrumb: breadcrumb => {
      if (typeof breadcrumb.data?.url === 'string') breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
      return breadcrumb;
    },
    ignoreErrors: ['CanceledError', 'AbortError', 'ERR_CANCELED', 'ERR_NETWORK'],
    tracesSampleRate: 0.1,
    // Only our own API gets the baggage and sentry-trace headers. Other hosts can reject them in CORS: Auth0 does,
    // which broke login. A string target matches any request URL that contains it.
    tracePropagationTargets: import.meta.env.VITE_API_BASE_URL ? [import.meta.env.VITE_API_BASE_URL as string] : [],
  });
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: import.meta.env.DEV
    ? (error, errorInfo) => console.error('Caught by error boundary:', error, errorInfo.componentStack)
    : () => {},
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
